/**
 * Item binding (plan 23 row 42, lane BI).
 *
 * Authored per item as `binding` in `content/items/*.toml`; the compiler
 * resolves the default and emits it on every `ITEM_DATABASE` row:
 *
 * - `none`      — can be dropped (and traded, if `tradeable`).
 * - `on_pickup` — bound when it enters the bag; never dropped. The default for
 *                 an item authored `tradeable = false`: dropping an untradeable
 *                 item would hand it to whoever picks it up, which is a trade.
 * - `on_equip`  — free until worn. Only bag entries can be dropped, so a worn
 *                 copy is never dropped; there is no per-copy "has been worn"
 *                 flag yet, so a copy back in the bag drops like `none`.
 * - `quest`     — a quest-only item; bound like `on_pickup`, labelled as such.
 */
export const ITEM_BINDINGS = ['none', 'on_pickup', 'on_equip', 'quest'] as const;

export type ItemBinding = (typeof ITEM_BINDINGS)[number];

/** Bindings that keep a bag copy in the bag: no dropping, no trading. */
export function isBindingBound(binding: ItemBinding | undefined): boolean {
  return binding === 'on_pickup' || binding === 'quest';
}

/** Player-facing line for a bound item (tooltip, inventory, drop refusal). */
export function boundItemLabel(binding: ItemBinding | undefined): string | null {
  if (binding === 'quest') return 'Quest item — bound to you';
  if (binding === 'on_pickup') return 'Bound to you';
  if (binding === 'on_equip') return 'Binds when worn';
  return null;
}
